'use client';

import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';

interface BackgroundVideoProps {
  src: string;
  className?: string;
}

export default function BackgroundVideo({
  src,
  className = '',
}: Readonly<BackgroundVideoProps>) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loaded, setLoaded] = useState(false);
  const shouldRenderVideo = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    if (!shouldRenderVideo) {
      setLoaded(false);
      return;
    }

    const vid = videoRef.current;
    if (!vid) return;

    vid.muted = true;
    vid.autoplay = true;
    vid.playsInline = true;

    const handleCanPlay = () => setLoaded(true);

    vid.addEventListener('canplay', handleCanPlay);
    vid.load();

    return () => {
      vid.removeEventListener('canplay', handleCanPlay);
    };
  }, [shouldRenderVideo]);

  return (
    <div
      className={`relative size-full overflow-hidden rounded-3xl ${className}`}
    >
      {shouldRenderVideo ? (
        <video
          ref={videoRef}
          className={`absolute inset-0 size-full object-cover transition-opacity duration-700 rounded-3xl ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loop
          muted
          playsInline
        >
          <source src={src} type="video/webm" />
        </video>
      ) : null}
    </div>
  );
}
